// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState, type ReactNode } from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  StyleSheet,
  View,
  type ImageResizeMode,
  type StyleProp,
  type ImageStyle,
} from 'react-native';

import appColors from '@/theme/colors';

import { withImageFetchSlot } from '../imageFetchQueue';

type AuthedImageProps = {
  uri: string | null;
  token: string | null | undefined;
  style?: StyleProp<ImageStyle>;
  resizeMode?: ImageResizeMode;
  accessibilityLabel?: string;
  testID?: string;
  /**
   * Rendered instead of the spinner while the bytes are in flight. The lightbox passes the
   * already-cached thumbnail here, so opening a photo shows something immediately rather than an
   * empty box — which matters because the web path cannot render progressively (see below).
   */
  placeholder?: ReactNode;
};

/**
 * Renders an image from a route that requires a bearer token.
 *
 * The tour photo byte route is authenticated on purpose (unlike the tour cover, which is anonymous
 * so it can render on the pre-sign-in invite screen). That creates a platform split, because a bare
 * `<Image source={{ uri }}>` sends no Authorization header:
 *
 * - **native**: React Native's `Image` honours `source.headers`, so the URI is used directly.
 * - **web**: react-native-web renders an `<img>` and silently drops `headers`, so the bytes have to
 *   be fetched here and handed over as a blob URL.
 *
 * Both paths live in this one component so no caller has to remember the difference. Note the
 * deliberate non-solution: passing the token as a query parameter would work everywhere but would
 * write access tokens into every access log along the way.
 */
export default function AuthedImage({
  uri,
  token,
  style,
  resizeMode = 'cover',
  accessibilityLabel,
  testID,
  placeholder,
}: AuthedImageProps) {
  const isWeb = Platform.OS === 'web';
  const [blobUri, setBlobUri] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isWeb || !uri || !token) {
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;
    setFailed(false);

    void (async () => {
      try {
        const blob = await withImageFetchSlot(async () => {
          // Re-check before spending a slot: by the time one frees up the tile may be gone.
          if (cancelled) {
            return null;
          }

          const response = await fetch(uri, { headers: { Authorization: `Bearer ${token}` } });
          if (!response.ok) {
            throw new Error(`image request failed with ${response.status}`);
          }
          return await response.blob();
        });

        if (cancelled || !blob) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setBlobUri(createdUrl);
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      // Without this every scroll through the grid leaks a blob for the lifetime of the tab.
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
      setBlobUri(null);
    };
  }, [isWeb, token, uri]);

  if (!uri || failed) {
    return <View style={[styles.placeholder, style]} testID={testID ? `${testID}-placeholder` : undefined} />;
  }

  if (isWeb) {
    if (!blobUri) {
      return (
        <View style={[styles.placeholder, style]} testID={testID ? `${testID}-loading` : undefined}>
          {placeholder ?? <ActivityIndicator size="small" color={appColors.text.muted} />}
        </View>
      );
    }

    return (
      <Image
        source={{ uri: blobUri }}
        style={style}
        resizeMode={resizeMode}
        accessibilityLabel={accessibilityLabel}
        testID={testID}
      />
    );
  }

  return (
    <Image
      source={{ uri, headers: token ? { Authorization: `Bearer ${token}` } : undefined }}
      style={style}
      resizeMode={resizeMode}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      onError={() => setFailed(true)}
    />
  );
}

const styles = StyleSheet.create({
  placeholder: {
    backgroundColor: appColors.surface.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
